import React, { forwardRef, Children, MouseEventHandler } from 'react';
import classNames from 'classnames';

import { isElementOfType } from '../../utilities/components';

import WpIcon from '../icon/WpIcon';
import { WpEmoji } from '../emoji';

import './WpButton.scss';

export type Color = 'primary' | 'danger' | 'white' | 'gray';
export const DEFAULT_COLOR = 'primary';

export type Variant = 'primary' | 'secondary' | 'tertiary' | 'link';
export const DEFAULT_VARIANT = 'primary';

export type Size = 'xs' | 'sm' | 'md' | 'lg';
export const DEFAULT_SIZE = 'md';

export type Type = 'submit' | 'button';
export const DEFAULT_TYPE = 'button';

export type DataAttributes = {
  [key: string]: string;
};

export interface Props {
  /**
   * Color of button . Options: 'primary' | 'danger' | 'white' | 'gray'
   * @default 'primary'
   */
  color?: Color;
  /**
   * Style of button. Options: 'primary' | 'secondary' | 'tertiary'
   * @default 'primary'
   */
  variant?: Variant;
  /**
   * Size of the button. Options: 'sm' | 'md' | 'lg'
   * @default 'md'
   */
  size?: Size;
  disabled?: boolean;
  loading?: boolean;
  block?: boolean;
  /** Callback when clicked */
  onClick?: (event: any) => void;
  children?: React.ReactNode | string | string[];
  icon?: boolean;
  rounded?: boolean;
  square?: boolean;
  slimPadding?: boolean;
  btnType?: Type;
  qaTest?: string;
  onMouseDown?;
  onMouseEnter?: MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: MouseEventHandler<HTMLButtonElement>;
  dataAttributes?: DataAttributes;
}

type ForwardedRef = {
  current: HTMLButtonElement;
};

const WpButton = forwardRef(
  (
    {
      color = DEFAULT_COLOR,
      variant = DEFAULT_VARIANT,
      size = DEFAULT_SIZE,
      block,
      onClick,
      children,
      disabled,
      loading,
      icon,
      rounded,
      square,
      slimPadding,
      btnType = DEFAULT_TYPE,
      qaTest,
      onMouseDown,
      onMouseEnter,
      onMouseLeave,
      dataAttributes = {},
    }: Props,
    ref: ForwardedRef
  ) => {
    const isDisabled = disabled || loading;

    // Checking to see if button contains WpIcon and add appropriate spacing
    const childrenLength = Children.count(children);
    const isOnlyIcon = icon || (children && childrenLength === 1 && isElementOfType(children, [WpIcon, WpEmoji]));
    const isFirstElementIcon = !isOnlyIcon && children && childrenLength > 1 && isElementOfType(children[0], WpIcon);
    const isLastElementIcon =
      !isOnlyIcon && children && childrenLength > 1 && isElementOfType(children[childrenLength - 1], WpIcon);

    const classNamePrefix = 'WpButton';
    const classes = classNames(
      classNamePrefix,
      `${classNamePrefix}--${size}`,
      // TODO (josh): don't like having to add the `variant` and `color` in the classname
      // but this is to distinguish between the color and variant
      `${classNamePrefix}--variant-${variant}`,
      `${classNamePrefix}--color-${color}`,
      isFirstElementIcon ? `${classNamePrefix}--icon-start` : false,
      isLastElementIcon ? `${classNamePrefix}--icon-end` : false,
      isOnlyIcon ? `${classNamePrefix}--icon-only` : false,
      rounded ? `${classNamePrefix}--rounded` : false,
      square ? `${classNamePrefix}--square` : false,
      slimPadding ? `${classNamePrefix}--slim-padding` : false,
      loading ? `${classNamePrefix}--loading` : false,
      block ? `${classNamePrefix}--block` : false
    );

    return (
      // eslint throws error if type is not set, also throw error if not a static string.
      // cannot be a variable that is dynamically set
      // eslint-disable-next-line react/button-has-type
      <button
        className={classes}
        onClick={onClick}
        ref={ref}
        type={btnType}
        disabled={isDisabled}
        data-test={qaTest}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        {...dataAttributes}
      >
        <span className={`${classNamePrefix}__content`}>{children}</span>
        {loading && (
          <span className={`${classNamePrefix}__spinner`}>
            <svg width={20} height={20} viewBox="0 0 50 50">
              <path d="M43.935 25.145c0-10.318-8.364-18.683-18.683-18.683-10.318 0-18.683 8.365-18.683 18.683h4.068c0-8.071 6.543-14.615 14.615-14.615s14.615 6.543 14.615 14.615h4.068z">
                <animateTransform
                  attributeType="xml"
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="0.6s"
                  repeatCount="indefinite"
                />
              </path>
            </svg>
          </span>
        )}
      </button>
    );
  }
);

export default WpButton;
